import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useNavigate, Link } from "react-router-dom";
import {
  Leaf, Flame, Sparkles, Clock, ArrowRight, RotateCcw, Soup, ChefHat,
  Activity, Target, Scale, Utensils, Share2, Lock, Copy, Check, Crown, ShoppingBasket, Mail, Lightbulb
} from "lucide-react";
import { NOTCH } from "@/constants/testIds";
import { useAuth, API, formatApiError } from "@/contexts/AuthContext";

const RESTRICTIONS = [
  { slug: "vegetarian", label: "Vegetarian" },
  { slug: "vegan", label: "Vegan" },
  { slug: "pescatarian", label: "Pescatarian" },
  { slug: "gluten-free", label: "Gluten-free" },
  { slug: "dairy-free", label: "Dairy-free" },
  { slug: "nut-free", label: "Nut-free" },
  { slug: "halal", label: "Halal" },
  { slug: "kosher", label: "Kosher" },
  { slug: "keto", label: "Keto" },
  { slug: "low-carb", label: "Low-carb" },
  { slug: "high-protein", label: "High-protein" },
  { slug: "low-sodium", label: "Low-sodium" },
];

const GOALS = [
  { v: "balanced", l: "Balanced eating" },
  { v: "weight-loss", l: "Weight loss" },
  { v: "muscle-gain", l: "Muscle gain" },
  { v: "maintain", l: "Maintain weight" },
  { v: "energy", l: "More energy" },
  { v: "gut-health", l: "Gut health" },
];

const CUISINES = [
  { v: "any", l: "Any cuisine" },
  { v: "indian", l: "Indian" },
  { v: "mediterranean", l: "Mediterranean" },
  { v: "east-asian", l: "East Asian" },
  { v: "middle-eastern", l: "Middle Eastern" },
  { v: "mexican", l: "Mexican" },
  { v: "american", l: "American" },
];

const ACTIVITY_OPTS = [
  { v: "sedentary", l: "Sedentary (desk job)" },
  { v: "light", l: "Light (1-2 workouts/wk)" },
  { v: "moderate", l: "Moderate (3-4 workouts/wk)" },
  { v: "very", l: "Very active (5-6 workouts/wk)" },
  { v: "extra", l: "Athlete-level" },
];

const MAX_CHARS = 200;

const HEALTH_FACTS = [
  "Protein takes more energy to digest than carbs or fat — roughly 20-30% of its calories go straight to digestion.",
  "Fibre feeds your gut bacteria, which in turn help regulate mood, immunity, and even how full you feel.",
  "Muscle burns more calories at rest than fat, so strength work compounds your daily energy use over time.",
  "Spreading protein evenly across meals builds muscle better than saving it all for dinner.",
  "A 20-30 minute walk after eating can meaningfully blunt the blood sugar spike from a carb-heavy meal.",
  "Most adults need 25-35g of fibre a day — the average diet lands closer to half that.",
  "Sleep debt raises ghrelin (hunger hormone) and lowers leptin (fullness hormone) — a rough night can mean a hungrier day.",
  "Chewing slowly gives your gut time to signal fullness to your brain — that lag is about 20 minutes.",
  "Legumes like chickpeas and lentils pack both protein and fibre, making them unusually efficient macro sources.",
  "Vitamin C helps your body absorb iron from plant sources — pair citrus with leafy greens or beans.",
  "Water needs jump with activity and heat, but thirst itself is already a sign of mild dehydration.",
  "Fermented foods like yogurt and kimchi introduce live cultures that support gut microbiome diversity.",
];

function HealthFactsLoader() {
  const [index, setIndex] = useState(() => Math.floor(Math.random() * HEALTH_FACTS.length));

  useEffect(() => {
    const id = setInterval(() => {
      setIndex((i) => (i + 1) % HEALTH_FACTS.length);
    }, 4500);
    return () => clearInterval(id);
  }, []);

  return (
    <div data-testid={NOTCH.factsLoader}
      className="mt-5 rounded-[1.5rem] bg-gradient-to-br from-[#F2EFE9] to-[#EAE6DD] border border-[#E5E2DC] p-5 sm:p-6 flex items-start gap-3">
      <div className="flex-shrink-0 h-8 w-8 rounded-full bg-[#2C4C3B]/10 flex items-center justify-center">
        <Lightbulb size={16} className="text-[#2C4C3B]" />
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#C06F52] mb-1.5">While we cook this up</div>
        <p key={index} className="text-sm text-[#333] leading-relaxed fact-fade">{HEALTH_FACTS[index]}</p>
      </div>
    </div>
  );
}

export default function Notch() {
  const { user, billing, refreshBilling } = useAuth();
  const navigate = useNavigate();

  // Profile
  const [age, setAge] = useState("");
  const [gender, setGender] = useState("female");
  const [heightCm, setHeightCm] = useState("");
  const [weightKg, setWeightKg] = useState("");
  const [activity, setActivity] = useState("moderate");
  const [profilePrefilled, setProfilePrefilled] = useState(false);

  // Prefill from the last profile the user generated a plan with, once.
  useEffect(() => {
    if (profilePrefilled || !user || !user.profile) return;
    const p = user.profile;
    if (p.age != null) setAge(String(p.age));
    if (p.gender) setGender(p.gender);
    if (p.height_cm != null) setHeightCm(String(p.height_cm));
    if (p.current_weight_kg != null) setWeightKg(String(p.current_weight_kg));
    if (p.activity_level) setActivity(p.activity_level);
    setProfilePrefilled(true);
  }, [user, profilePrefilled]);

  // Plan
  const [selected, setSelected] = useState(new Set());
  const [customText, setCustomText] = useState("");
  const [goal, setGoal] = useState("balanced");
  const [cuisine, setCuisine] = useState("any");
  const [mealsCount, setMealsCount] = useState(3);
  const [days, setDays] = useState(1);

  const [targets, setTargets] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [results, setResults] = useState(null);
  const [activeDay, setActiveDay] = useState(1);
  const [shareLink, setShareLink] = useState("");
  const [copied, setCopied] = useState(false);
  const resultsRef = useRef(null);

  const profileReady = age && heightCm && weightKg && gender && activity;

  useEffect(() => {
    if (!profileReady) { setTargets(null); return; }
    const ctrl = new AbortController();
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.post(`${API}/targets`, {
          profile: {
            age: Number(age), gender, height_cm: Number(heightCm),
            current_weight_kg: Number(weightKg), activity_level: activity,
          }, goal,
        }, { signal: ctrl.signal });
        setTargets(data);
      } catch {}
    }, 300);
    return () => { clearTimeout(t); ctrl.abort(); };
  }, [age, gender, heightCm, weightKg, activity, goal, profileReady]);

  const togglePill = (slug) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug); else next.add(slug);
      return next;
    });
  };

  const charCount = customText.length;
  const counterColor = charCount > MAX_CHARS - 20 ? "text-[#C06F52]" : "text-[#999999]";
  const canSubmit = useMemo(
    () => profileReady && (selected.size > 0 || customText.trim().length > 0),
    [profileReady, selected, customText]
  );

  const isPro = billing?.subscription_status === "active";
  const outOfFree = !isPro && billing && billing.free_remaining <= 0;

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError("");
    setShareLink("");
    if (!profileReady) { setError("Tell us your age, height and weight first — we'll do the rest."); return; }
    if (selected.size === 0 && !customText.trim()) { setError("Pick at least one restriction or tell us about your diet below."); return; }
    if (outOfFree) { navigate("/pricing"); return; }
    setLoading(true); setResults(null);
    try {
      const { data } = await axios.post(`${API}/suggest`, {
        profile: {
          age: Number(age), gender, height_cm: Number(heightCm),
          current_weight_kg: Number(weightKg), activity_level: activity,
        },
        restrictions: Array.from(selected),
        custom_diet_text: customText.trim(),
        goal, cuisine,
        meals_per_day: Number(mealsCount),
        days: Number(days),
      });
      setResults(data); setActiveDay(1);
      await refreshBilling();
      setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
    } catch (err) {
      if (err?.response?.status === 402) { navigate("/pricing"); return; }
      setError(formatApiError(err?.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const reset = () => {
    setSelected(new Set()); setCustomText("");
    setGoal("balanced"); setCuisine("any"); setMealsCount(3); setDays(1);
    setAge(""); setHeightCm(""); setWeightKg("");
    setGender("female"); setActivity("moderate");
    setTargets(null); setResults(null); setError(""); setShareLink("");
  };

  const sharePlan = async () => {
    if (!results) return;
    try {
      const { data } = await axios.post(`${API}/plans/${results.id}/share`);
      const url = `${window.location.origin}/share/${data.share_id}`;
      setShareLink(url);
      try { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 2500); } catch {}
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || "Couldn't create share link");
    }
  };

  return (
    <div className="relative z-10">
      <Hero />
      <main className="max-w-6xl mx-auto px-4 sm:px-8 pb-16 sm:pb-24">
        {outOfFree && <FreeLimitBanner />}

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14">
          <section className="lg:col-span-7 fade-up">
            <form onSubmit={handleSubmit} className="space-y-10">
              <div>
                <SectionLabel num="01" title="About you" subtitle="The basis of every suggestion. No target weight to fill — we calculate it." />
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4 mt-6">
                  <NumberField testid={NOTCH.ageInput} label="Age" value={age} onChange={setAge} min={12} max={100} unit="yrs" />
                  <SelectField testid={NOTCH.genderSelect} label="Gender" value={gender} onChange={setGender}
                    options={[{v:"female",l:"Female"},{v:"male",l:"Male"},{v:"other",l:"Other"}]} />
                  <NumberField testid={NOTCH.heightInput} label="Height" value={heightCm} onChange={setHeightCm} min={120} max={230} unit="cm" step="0.1" />
                  <NumberField testid={NOTCH.weightInput} label="Current weight" value={weightKg} onChange={setWeightKg} min={30} max={300} unit="kg" step="0.1" />
                  <div className="col-span-2 sm:col-span-1">
                    <SelectField testid={NOTCH.activitySelect} label="Activity" value={activity} onChange={setActivity} options={ACTIVITY_OPTS} />
                  </div>
                </div>
                {targets && <TargetsPreviewCard targets={targets} />}
              </div>

              <div>
                <SectionLabel num="02" title="Food restrictions" subtitle="Tap any that apply. We'll respect every one." />
                <div className="flex flex-wrap gap-2.5 mt-6" data-testid="restrictions-pill-group">
                  {RESTRICTIONS.map((r) => {
                    const active = selected.has(r.slug);
                    return (
                      <button type="button" key={r.slug}
                        data-testid={NOTCH.pill(r.slug)}
                        onClick={() => togglePill(r.slug)}
                        className={`inline-flex items-center px-4 sm:px-5 py-2.5 rounded-full text-sm font-medium border transition-all duration-200
                          ${active ? "bg-[#2C4C3B] border-[#2C4C3B] text-white shadow-sm"
                                    : "bg-white border-[#E5E2DC] text-[#444] hover:border-[#2C4C3B] hover:text-[#2C4C3B]"}`}>
                        {r.label}
                      </button>
                    );
                  })}
                </div>
                <div className="relative w-full mt-7">
                  <label htmlFor="custom-diet" className="block text-sm font-medium text-[#222] mb-3">
                    Anything else? Tell us about your diet
                  </label>
                  <input
                    id="custom-diet" type="text" maxLength={MAX_CHARS}
                    data-testid={NOTCH.customDietInput}
                    value={customText} onChange={(e) => setCustomText(e.target.value)}
                    placeholder="e.g. I'm eggetarian, I avoid onion and garlic, I only eat home-cooked food..."
                    className="w-full rounded-full border border-[#D1CDD2] bg-white pl-5 sm:pl-6 pr-20 sm:pr-24 py-3.5 text-sm sm:text-base text-[#222] placeholder:text-[#999] focus:outline-none focus:ring-2 focus:ring-[#2C4C3B]/20 focus:border-[#2C4C3B] transition-all"
                  />
                  <span data-testid={NOTCH.customDietCounter}
                    className={`absolute right-5 bottom-3.5 text-xs ${counterColor} pointer-events-none tracking-wide`}>
                    {charCount}/{MAX_CHARS}
                  </span>
                </div>
              </div>

              <div>
                <SectionLabel num="03" title="Your plan" subtitle="Pick a goal, how many meals, and how many days." />
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mt-6">
                  <SelectField testid={NOTCH.goalSelect} label="Goal" value={goal} onChange={setGoal} options={GOALS} />
                  <SelectField testid={NOTCH.cuisineSelect} label="Cuisine" value={cuisine} onChange={setCuisine} options={CUISINES} />
                  <SelectField testid={NOTCH.mealsCountSelect} label="Meals / day" value={String(mealsCount)} onChange={(v) => setMealsCount(Number(v))}
                    options={[{v:"2",l:"2 meals"},{v:"3",l:"3 meals"},{v:"4",l:"4 meals"},{v:"5",l:"5 meals"}]} />
                  <SelectField testid={NOTCH.daysSelect} label="Plan length" value={String(days)} onChange={(v)=>setDays(Number(v))}
                    options={[{v:"1",l:"1 day"},{v:"3",l:"3 days"},{v:"7",l:"7 days (full week)"}]} />
                </div>
              </div>

              {error && (
                <div data-testid={NOTCH.errorBanner} className="rounded-2xl bg-[#FBEDE7] border border-[#E9C2B3] text-[#7a3320] px-5 py-3 text-sm">
                  {error}
                </div>
              )}

              <div className="flex flex-wrap items-center gap-3 sm:gap-4 pt-2">
                <button type="submit" disabled={loading || !canSubmit}
                  data-testid={NOTCH.generateBtn}
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-7 sm:px-8 py-4 rounded-full bg-[#2C4C3B] text-white text-base font-medium hover:bg-[#1E3629] transition-all duration-300 focus:ring-4 focus:ring-[#2C4C3B]/20 hover:shadow-lg hover:shadow-[#2C4C3B]/20 disabled:opacity-50 disabled:cursor-not-allowed">
                  {loading ? (<><span className="h-2 w-2 rounded-full bg-white animate-pulse" />{days > 1 ? "Crafting your week — this can take up to a minute…" : "Crafting your plate…"}</>)
                          : (<><Sparkles size={18} />Get my suggestions<ArrowRight size={18} /></>)}
                </button>
                <button type="button" onClick={reset} data-testid={NOTCH.resetBtn}
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm text-[#666] hover:text-[#222] transition-colors">
                  <RotateCcw size={14} />Start over
                </button>
              </div>

              {loading && <HealthFactsLoader />}
            </form>
          </section>

          <aside className="lg:col-span-5 fade-up delay-2">
            <AboutCard />
          </aside>
        </div>

        <div ref={resultsRef}>
          {results && (
            <Results
              results={results}
              activeDay={activeDay}
              setActiveDay={setActiveDay}
              onShare={sharePlan}
              shareLink={shareLink}
              copied={copied}
            />
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}

// ---------- subcomponents (mostly carried over from prev) ----------
function FreeLimitBanner() {
  return (
    <div className="mb-10 rounded-[1.5rem] bg-[#2C4C3B] text-[#F9F8F6] p-5 sm:p-6 flex flex-wrap items-center gap-4 justify-between fade-up">
      <div className="flex items-start gap-3">
        <Lock size={18} className="mt-0.5 flex-shrink-0" />
        <div>
          <p className="font-medium text-base">You've used all 6 free plans.</p>
          <p className="text-sm text-[#D7E0DA] mt-0.5">Subscribe to Notch Pro for unlimited plans + photo log.</p>
        </div>
      </div>
      <a href="/pricing" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#C06F52] text-white text-sm font-medium hover:bg-[#a85a3f] transition-colors">
        <Crown size={14} /> Upgrade to Pro — ₹49/mo
      </a>
    </div>
  );
}

function Hero() {
  return (
    <section className="max-w-6xl mx-auto px-4 sm:px-8 pt-8 sm:pt-12 pb-10 sm:pb-14">
      <div className="grid grid-cols-1 md:grid-cols-12 items-end gap-8">
        <div className="md:col-span-8 fade-up">
          <div className="inline-flex items-center gap-2 text-[10px] sm:text-xs uppercase tracking-[0.18em] text-[#C06F52] mb-4 sm:mb-5">
            <span className="h-px w-8 bg-[#C06F52]" />Macro-clean bowls, your way
          </div>
          <h1 className="font-serif-display text-4xl sm:text-6xl lg:text-7xl leading-[1.05] sm:leading-[1.02] text-[#2C4C3B]">
            Eat in a way<br />that <em className="text-[#C06F52] not-italic italic">actually</em> fits you.
          </h1>
          <p className="mt-5 sm:mt-6 max-w-xl text-[#666] text-base sm:text-lg leading-relaxed">
            Share a few details — we'll calculate your target weight and craft high-protein, high-fibre bowls in the style of a well-kept WhatsApp recipe diary.
          </p>
        </div>
        <div className="md:col-span-4 hidden md:block fade-up delay-2">
          <div className="relative rounded-[2rem] overflow-hidden border border-[#E5E2DC] shadow-[0_20px_60px_-30px_rgba(44,76,59,0.35)]">
            <img src="https://images.unsplash.com/photo-1546069901-ba9599a7e63c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwyfHxoZWFsdGh5JTIwbWVhbCUyMG9yZ2FuaWMlMjBib3dsfGVufDB8fHx8MTc4MjYyMDE2Nnww&ixlib=rb-4.1.0&q=85"
              alt="A bright, fresh organic bowl" className="w-full h-64 object-cover" />
            <div className="absolute bottom-4 left-4 right-4 bg-white/85 backdrop-blur-md rounded-2xl px-4 py-3 flex items-center gap-3">
              <ChefHat size={18} className="text-[#2C4C3B]" />
              <p className="text-xs text-[#222] leading-snug">Smart suggestions — <span className="font-medium">tuned to your plate.</span></p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export function SectionLabel({ num, title, subtitle }) {
  return (
    <div className="flex items-baseline gap-3 sm:gap-4">
      <span className="text-[11px] sm:text-xs tracking-[0.22em] text-[#C06F52]">{num}</span>
      <div>
        <h2 className="font-serif-display text-2xl sm:text-4xl text-[#222] leading-tight">{title}</h2>
        {subtitle && <p className="text-xs sm:text-sm text-[#666] mt-1">{subtitle}</p>}
      </div>
    </div>
  );
}

export function SelectField({ label, value, onChange, options, testid }) {
  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-[0.16em] text-[#888] mb-2">{label}</span>
      <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full appearance-none rounded-full border border-[#D1CDD2] bg-white px-4 sm:px-5 py-3 pr-9 text-sm text-[#222] focus:outline-none focus:ring-2 focus:ring-[#2C4C3B]/20 focus:border-[#2C4C3B] cursor-pointer transition-all"
        style={{ backgroundImage:"url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'><path fill='none' stroke='%23666' stroke-width='1.5' d='M1 1l5 5 5-5'/></svg>\")", backgroundRepeat:"no-repeat", backgroundPosition:"right 16px center" }}>
        {options.map((o) => (<option key={o.v} value={o.v}>{o.l}</option>))}
      </select>
    </label>
  );
}

export function NumberField({ label, value, onChange, min, max, unit, testid, step = "1" }) {
  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-[0.16em] text-[#888] mb-2">{label}</span>
      <div className="relative">
        <input type="number" data-testid={testid} value={value} min={min} max={max} step={step} inputMode="decimal"
          onChange={(e) => onChange(e.target.value)} placeholder="–"
          className="w-full rounded-full border border-[#D1CDD2] bg-white px-4 sm:px-5 py-3 pr-12 text-sm text-[#222] placeholder:text-[#bbb] focus:outline-none focus:ring-2 focus:ring-[#2C4C3B]/20 focus:border-[#2C4C3B] transition-all" />
        {unit && <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xs text-[#999] pointer-events-none">{unit}</span>}
      </div>
    </label>
  );
}

function TargetsPreviewCard({ targets }) {
  const positive = targets.delta_kg > 0.5;
  const negative = targets.delta_kg < -0.5;
  const deltaText = positive ? `${targets.delta_kg.toFixed(1)} kg to lose`
                  : negative ? `${Math.abs(targets.delta_kg).toFixed(1)} kg to gain`
                  : "right on target";
  return (
    <div data-testid={NOTCH.targetsCard}
      className="mt-6 rounded-[1.75rem] bg-gradient-to-br from-[#F2EFE9] to-[#EAE6DD] border border-[#E5E2DC] p-5 sm:p-6">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[#C06F52] mb-3">
        <Target size={12} /> System-calculated targets
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-3">
        <Stat icon={<Scale size={14} />} label="Target weight" value={`${targets.target_weight_kg} kg`} sub={deltaText} testid={NOTCH.targetWeight} highlight />
        <Stat icon={<Activity size={14} />} label="BMI" value={targets.bmi} sub={targets.bmi_category} testid={NOTCH.targetBmi} />
        <Stat icon={<Flame size={14} />} label="Daily kcal" value={targets.daily_target_kcal} sub={`TDEE ${targets.tdee_kcal}`} testid={NOTCH.targetKcal} />
        <Stat icon={<Utensils size={14} />} label="Protein" value={`${targets.protein_target_g} g`} sub="per day" testid={NOTCH.targetProtein} />
      </div>
    </div>
  );
}

function Stat({ icon, label, value, sub, testid, highlight }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.16em] text-[#888] mb-1">{icon}{label}</div>
      <div data-testid={testid} className={`font-serif-display ${highlight ? "text-[#2C4C3B]" : "text-[#222]"} text-2xl sm:text-3xl leading-none`}>
        {value}
      </div>
      {sub && <div className="text-[11px] text-[#666] mt-1.5 capitalize">{sub}</div>}
    </div>
  );
}

function AboutCard() {
  return (
    <div id="about" data-testid={NOTCH.aboutCard}
      className="rounded-[1.75rem] sm:rounded-[2rem] bg-[#F2EFE9] border border-[#E5E2DC] p-6 sm:p-10 lg:sticky lg:top-8">
      <div className="flex items-center gap-2 text-[10px] sm:text-xs uppercase tracking-[0.2em] text-[#C06F52] mb-4">
        <Soup size={14} /> About this app
      </div>
      <h3 className="font-serif-display text-2xl sm:text-3xl text-[#2C4C3B] mb-4 sm:mb-5 leading-tight">Personalized eating, gently done.</h3>
      <div className="space-y-4 text-[14px] sm:text-[15px] leading-relaxed text-[#444]">
        <p>Notch helps you discover meals that fit your dietary preferences and restrictions. Pick the tags that describe how you eat, set a goal, and we'll generate suggestions that respect every choice — from broad lifestyles like vegan or keto to specific allergies and cuisines.</p>
        <p>You can also tell us more about your eating habits in your own words — whether you're eggetarian, avoid certain ingredients for personal or religious reasons, or follow any other dietary practice not listed above. We'll do our best to factor this into your suggestions, but please note that open-text inputs are not always perfectly interpreted and may not be fully reflected in every recommendation.</p>
        <p>Suggestions are a starting point, not medical advice. If you have a serious condition or allergy, always confirm with a qualified professional before changing your diet.</p>
      </div>
      <div className="mt-6 pt-6 border-t border-[#E5E2DC] flex items-center gap-3 text-xs text-[#666]">
        <Flame size={14} className="text-[#C06F52]" />Built to read nuance — your habits, in your own words.
      </div>
    </div>
  );
}

export function Results({ results, activeDay, setActiveDay, onShare, shareLink, copied }) {
  const day = results.days?.find(d => d.day === activeDay) || results.days?.[0];
  return (
    <section data-testid={NOTCH.resultsSection} className="mt-16 sm:mt-20 pt-10 sm:pt-12 border-t border-[#E5E2DC]">
      <div className="flex items-end justify-between flex-wrap gap-6 mb-6 sm:mb-8">
        <div>
          <div className="text-[10px] sm:text-xs uppercase tracking-[0.2em] text-[#C06F52] mb-2">04 · Your suggestions</div>
          <h2 className="font-serif-display text-3xl sm:text-5xl text-[#2C4C3B] leading-tight">Today, on your plate.</h2>
          <div className="mt-3 text-xs sm:text-sm text-[#666]">
            Tuned to <span className="text-[#2C4C3B] font-medium">{results.targets.daily_target_kcal} kcal</span> /{" "}
            <span className="text-[#2C4C3B] font-medium">{results.targets.protein_target_g} g protein</span> · target weight{" "}
            <span className="text-[#2C4C3B] font-medium">{results.targets.target_weight_kg} kg</span>
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          {onShare && (
            <button data-testid={NOTCH.shareBtn} onClick={onShare}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#E5E2DC] bg-white text-sm text-[#2C4C3B] hover:bg-[#F2EFE9] transition-colors">
              {copied ? (<><Check size={14}/>Link copied</>) : (<><Share2 size={14}/>Share plan</>)}
            </button>
          )}
          {shareLink && <span data-testid={NOTCH.shareLink} className="text-[11px] text-[#888] break-all max-w-[260px]">{shareLink}</span>}
          {results.note && (
            <p data-testid={NOTCH.resultsNote} className="max-w-md text-sm text-[#666] italic border-l-2 border-[#C06F52] pl-4 mt-2">{results.note}</p>
          )}
        </div>
      </div>

      {results.days && results.days.length > 1 && (
        <div className="flex flex-wrap gap-2 mb-7">
          {results.days.map((d, i) => (
            <button key={d.day} data-testid={NOTCH.dayTab(i)} onClick={() => setActiveDay(d.day)}
              className={`px-4 py-1.5 rounded-full text-xs font-medium border transition-all
                ${activeDay === d.day ? "bg-[#2C4C3B] border-[#2C4C3B] text-white"
                                       : "bg-white border-[#E5E2DC] text-[#666] hover:border-[#2C4C3B] hover:text-[#2C4C3B]"}`}>
              {d.label || `Day ${d.day}`}
            </button>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6">
        {(day?.meals || []).map((m, i) => (
          <article key={i} data-testid={NOTCH.mealCard(i)}
            className="bg-white rounded-[1.75rem] sm:rounded-[2rem] p-6 sm:p-7 border border-[#E5E2DC] shadow-[0_8px_30px_-12px_rgba(0,0,0,0.08)] hover:-translate-y-1 transition-transform duration-300 fade-up">
            <div className="flex items-center justify-between mb-3">
              <span className="text-[10px] uppercase tracking-[0.18em] text-[#888]">{m.meal_type}</span>
              <span className="inline-flex items-center gap-1 text-xs text-[#666]"><Clock size={12} />{m.prep_time_min} min</span>
            </div>
            <h3 className="font-serif-display text-xl sm:text-2xl text-[#2C4C3B] leading-tight mb-3">{m.name}</h3>
            <p className="text-sm text-[#C06F52] font-medium flex items-start gap-2 mb-5">
              <Sparkles size={14} className="mt-0.5 flex-shrink-0" /><span>{m.why_it_fits}</span>
            </p>
            <div className="mb-5">
              <p className="text-[10px] uppercase tracking-[0.16em] text-[#888] mb-2">Ingredients</p>
              <ul className="flex flex-wrap gap-2">
                {m.ingredients.map((ing, idx) => (
                  <li key={idx} className="text-xs bg-[#F2EFE9] text-[#444] px-3 py-1.5 rounded-full">{ing}</li>
                ))}
              </ul>
            </div>
            {m.prep_steps && m.prep_steps.length > 0 && (
              <div className="mb-5">
                <p className="text-[10px] uppercase tracking-[0.16em] text-[#888] mb-2">Method</p>
                <ol className="space-y-1.5 text-sm text-[#444] list-decimal list-inside marker:text-[#C06F52]">
                  {m.prep_steps.map((s, idx) => <li key={idx} className="leading-snug">{s}</li>)}
                </ol>
              </div>
            )}
            <div className="flex flex-wrap gap-5 pt-5 border-t border-[#E5E2DC]">
              <Macro label="kcal" value={m.macros.calories} />
              <Macro label="protein" value={`${m.macros.protein_g}g`} />
              <Macro label="carbs" value={`${m.macros.carbs_g}g`} />
              <Macro label="fat" value={`${m.macros.fat_g}g`} />
              {m.macros.fiber_g != null && <Macro label="fibre" value={`${m.macros.fiber_g}g`} />}
            </div>
          </article>
        ))}
      </div>

      {results.grocery_list && results.grocery_list.length > 0 && (
        <div data-testid={NOTCH.groceryListSection} className="mt-10 sm:mt-12 pt-8 sm:pt-10 border-t border-[#E5E2DC]">
          <div className="flex items-center gap-2.5 mb-6">
            <ShoppingBasket size={18} className="text-[#C06F52]" />
            <h3 className="font-serif-display text-2xl sm:text-3xl text-[#2C4C3B]">Grocery list</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {results.grocery_list.map((group, i) => (
              <div key={i} className="bg-white rounded-[1.5rem] p-5 sm:p-6 border border-[#E5E2DC]">
                <p className="text-[10px] uppercase tracking-[0.18em] text-[#888] mb-3">{group.category || "Other"}</p>
                <ul className="space-y-2">
                  {(group.items || []).map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-[#333]">
                      <span className="mt-1.5 h-1 w-1 rounded-full bg-[#C06F52] flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

function Macro({ label, value }) {
  return (
    <div>
      <div className="text-lg font-medium text-[#2C4C3B] leading-none">{value}</div>
      <div className="text-[10px] uppercase tracking-[0.16em] text-[#888] mt-1">{label}</div>
    </div>
  );
}

function Footer() {
  return (
    <footer className="max-w-6xl mx-auto px-4 sm:px-8 py-8 sm:py-10 border-t border-[#E5E2DC]">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 pb-6 mb-6 border-b border-[#E5E2DC]">
        <div>
          <h3 className="font-serif-display text-xl text-[#2C4C3B]">Get in touch</h3>
          <p className="text-sm text-[#666] mt-1 max-w-md">
            Questions, feedback, or just want to say hi? We'd love to hear from you.
          </p>
        </div>
        <a
          href="mailto:hi@sahana.cc"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#2C4C3B] text-white text-sm font-medium hover:bg-[#1E3629] transition-colors w-fit"
        >
          <Mail size={14} /> hi@sahana.cc
        </a>
      </div>
      <div className="text-xs text-[#888] flex flex-wrap items-center justify-between gap-3">
        <span>© {new Date().getFullYear()} Notch · made with care</span>
        <div className="flex items-center gap-4">
          <Link to="/privacy" className="hover:text-[#2C4C3B] hover:underline">Privacy &amp; terms</Link>
          <span>Not medical advice.</span>
        </div>
      </div>
    </footer>
  );
}
